export function createFukushizaiContextCommitService(deps) {
    const {
        entrydetailRef,
        selectedFukushizaiContextRef,
        selectedQualityContextRef,
        fukushizaiDirtyPanelRef,
        gridlist,
        PANEL_TYPE,
        isEmpty,
        isEnabled,
        DicVal,
        normalizeFukushizaiContext,
        canonicalPanelType,
        isQualityPanelType,
        isFukushizaiContextDisplayPanelInfo,
        getPanelType,
        getDetailRowsByPanelType,
        hasFukushizaiBaseDirty,
        syncFukushizaiBaseDirtyFieldsToDetail,
        readQualityBaseDirtyState,
        clearQualityBaseDirtyFields,
        applyQualityBaseUpdatesToCurrentContext,
        isFukushizaiDirtyPanel,
        clearFukushizaiDirtyPanel,
        GridApi,
        isGridDomReady,
        GridData,
        readGridRowsPreferApi,
        toQualitySaveRows,
        flushFukushizaiDisplayGridsToDetail,
        debugFukushizaiSyncLog
    } = deps;

    // entrydata 上の副資材代表値を保存前同期の流れへ寄せます。
    function syncFukushizaiRepresentativeRowFromEntry(entryObj) {
        if (isEmpty(entryObj)) return false;
        const result = syncFukushizaiBaseDirtyFieldsToDetail({
            context: selectedFukushizaiContextRef.current,
            timing: "save-before-payload",
            reason: "legacy-representative-sync"
        });
        return isEnabled(DicVal(result, "changed", false));
    }

    // 品質表示 BASE の dirty 状態を detail へ同期します。
    function syncQualityBaseDirtyFieldsToDetail(options = {}) {
        // 品質表示は副資材と独立した選択のため、既定は品質側 context を使う。
        const context = normalizeFukushizaiContext(DicVal(options, "context", selectedQualityContextRef.current));
        const dirtyState = readQualityBaseDirtyState(context);
        const dirtyFields = dirtyState.fields || {};
        const dirtyFieldIds = Object.keys(dirtyFields);

        // dirty 項目が無い場合は何もしません。
        if (dirtyFieldIds.length === 0) {
            return { changed: false, updatedCount: 0, context, skipReason: "no-dirty-fields" };
        }

        const result = applyQualityBaseUpdatesToCurrentContext(dirtyFields, context);
        clearQualityBaseDirtyFields(context);
        return result;
    }

    // Grid 編集中のセルを確定させます。
    function stopFukushizaiGridEditing(panelInfo) {
        const api = GridApi(panelInfo);
        if (!isEmpty(api) && typeof api.stopEditing === "function") api.stopEditing();
    }

    // stopEditing 後に Grid 側の反映が落ち着くまで 1tick 待機します。
    function waitFukushizaiGridEditCommitted() {
        return new Promise((resolve) => {
            const raf =
                typeof window !== "undefined" && typeof window.requestAnimationFrame === "function"
                    ? window.requestAnimationFrame.bind(window)
                    : typeof globalThis !== "undefined" && typeof globalThis.requestAnimationFrame === "function"
                        ? globalThis.requestAnimationFrame.bind(globalThis)
                        : null;

            // requestAnimationFrame が使える環境では描画タイミングまで待機します。
            if (typeof raf === "function") {
                raf(() => resolve());
                return;
            }

            setTimeout(resolve, 0);
        });
    }

    // 品質表示 Grid 1 枚分を保存用 detail へ戻します。
    function syncQualityDisplayGridToDetail(panelInfo, rows = null, contextOverride = null) {
        const type = canonicalPanelType(DicVal(panelInfo, "type", ""));

        // 洗濯表示は専用同期を使うため、ここでは対象外にします。
        if (type === PANEL_TYPE.WASH) return false;

        // 品質表示 BASE と非品質パネルは対象外です。
        if (!isQualityPanelType(type) || type === PANEL_TYPE.QUALITY_BASE) return false;

        // 品質表示のグループは副資材と独立して選択されるため、既定は品質表示側の context を使う。
        const context = normalizeFukushizaiContext(contextOverride || selectedQualityContextRef.current);
        if (context.itemCd === "" || context.groupNo === "") return false;

        const sourceRows = Array.isArray(rows) ? rows : GridData(panelInfo);
        const before = JSON.stringify(getDetailRowsByPanelType(entrydetailRef.current, type));
        toQualitySaveRows(sourceRows, type, context);
        const after = JSON.stringify(getDetailRowsByPanelType(entrydetailRef.current, type));
        return before !== after;
    }

    // 品質表示系 Grid の変更を保存前にまとめて detail へ戻します。
    function flushQualityDisplayGridsToDetail(options = {}) {
        // 品質表示のグループは副資材と独立して選択されるため、既定は品質表示側の context を使う。
        const context = normalizeFukushizaiContext(DicVal(options, "context", selectedQualityContextRef.current));
        if (!Array.isArray(gridlist) || gridlist.length === 0 || context.itemCd === "" || context.groupNo === "") return false;

        const dirtyOnly = isEnabled(DicVal(options, "dirtyOnly", false));
        const stopEditing = DicVal(options, "stopEditing", true) !== false;
        let changed = false;

        gridlist.forEach((panelInfo) => {
            const type = canonicalPanelType(DicVal(panelInfo, "type", ""));

            // 洗濯表示 124 は WashGrid 専用のため汎用品質同期から除外します。
            if (type === PANEL_TYPE.WASH) return;

            // 品質表示 BASE と非品質パネルは対象外です。
            if (!isQualityPanelType(type) || type === PANEL_TYPE.QUALITY_BASE) return;

            // dirtyOnly の場合は対象 context で編集されたパネルだけ同期します。
            if (dirtyOnly && !isFukushizaiDirtyPanel(panelInfo, context)) return;

            if (stopEditing) {
                const api = GridApi(panelInfo);
                if (!isEmpty(api) && typeof api.stopEditing === "function") {
                    try {
                        api.stopEditing();
                    } catch (error) {
                        console.error(error);
                    }
                }
            }

            const rows = readGridRowsPreferApi(panelInfo, { stopEditing: false });
            const apiReady = !isEmpty(GridApi(panelInfo)) && isGridDomReady(panelInfo);

            // API 未初期化かつローカル rows も無い場合は同期対象にしません。
            if (!apiReady && (!Array.isArray(rows) || rows.length === 0)) return;

            if (syncQualityDisplayGridToDetail(panelInfo, rows, context)) changed = true;
            clearFukushizaiDirtyPanel(panelInfo, context);
        });

        return changed;
    }

    // context 切替前に BASE/表示 Grid の未反映変更をまとめて commit します。
    //
    // options.domain:
    //   "fukushizai" ... 副資材(113-117)のみ確定(副資材のアイテム/グループ切替時)
    //   "quality"    ... 品質表示(118-124)のみ確定(品質表示のアイテム/グループ切替時)
    //   未指定       ... 両方確定(従来互換)
    // 切替元ドメイン以外を oldContext で確定すると、別グループ番号で detail へ
    // 書き込む恐れがあるため、切替時は必ず domain を指定すること。
    async function commitFukushizaiActiveContextBeforeLeave(oldContext, options = {}) {
        const domain = String(DicVal(options, "domain", "") ?? "");
        const includeFukushizai = domain !== "quality";
        const includeQuality = domain !== "fukushizai";
        const context = normalizeFukushizaiContext(oldContext);
        const dirtyKeys = Object.keys(fukushizaiDirtyPanelRef.current || {});
        const panels = (Array.isArray(gridlist) ? gridlist : [])
            .filter((panelInfo) =>
                (includeFukushizai && isFukushizaiContextDisplayPanelInfo(panelInfo))
                || (includeQuality && isQualityPanelType(getPanelType(panelInfo)) && getPanelType(panelInfo) !== PANEL_TYPE.QUALITY_BASE)
            )
            .filter((panelInfo) => isFukushizaiDirtyPanel(panelInfo, context));
        const baseDirtyBeforeLeave = includeFukushizai ? hasFukushizaiBaseDirty(context) : false;
        const baseResult = includeFukushizai
            ? syncFukushizaiBaseDirtyFieldsToDetail({
                context,
                timing: "group-before-switch",
                reason: "before-leave"
            })
            : null;
        const qualityBaseResult = includeQuality
            ? syncQualityBaseDirtyFieldsToDetail({
                context,
                timing: "group-before-switch",
                reason: "before-leave"
            })
            : null;

        // dirty Grid が無い場合は BASE 同期結果だけ返します。
        if (panels.length === 0) {
            return isEnabled(DicVal(baseResult, "changed", false)) || isEnabled(DicVal(qualityBaseResult, "changed", false));
        }

        panels.forEach((panelInfo) => stopFukushizaiGridEditing(panelInfo));
        await waitFukushizaiGridEditCommitted();

        const result = includeFukushizai
            ? flushFukushizaiDisplayGridsToDetail({
                context,
                dirtyOnly: true,
                stopEditing: false,
                timing: "group-before-switch"
            })
            : null;
        const qualityChanged = includeQuality
            ? flushQualityDisplayGridsToDetail({
                context,
                dirtyOnly: true,
                stopEditing: false
            })
            : false;
        const safeResult = !isEmpty(result) && typeof result === "object"
            ? result
            : { changed: false, updatedCount: 0, context };

        debugFukushizaiSyncLog("[Kakousiji][fukushizai group change]", {
            oldContext: context,
            newContext: selectedFukushizaiContextRef.current,
            dirtyKeys,
            baseDirtyBeforeLeave,
            committedPanels: panels.map((panelInfo) => canonicalPanelType(DicVal(panelInfo, "type", ""))),
            updatedCount: DicVal(safeResult, "updatedCount", 0),
            selectedContextAfter: selectedFukushizaiContextRef.current
        });

        return isEnabled(DicVal(baseResult, "changed", false))
            || isEnabled(DicVal(qualityBaseResult, "changed", false))
            || isEnabled(DicVal(safeResult, "changed", false))
            || qualityChanged;
    }

    return {
        syncFukushizaiRepresentativeRowFromEntry,
        syncQualityBaseDirtyFieldsToDetail,
        stopFukushizaiGridEditing,
        waitFukushizaiGridEditCommitted,
        syncQualityDisplayGridToDetail,
        flushQualityDisplayGridsToDetail,
        commitFukushizaiActiveContextBeforeLeave
    };
}
